import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import IdCard from "../../../../../../../assets/images/Vector.svg";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import {
  validateField,
  getFieldMax,
  getFieldMin,
} from "../../../../helper/fieldHelper";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import type { FieldInfo } from "../models/fieldInfo";

export function SenderInfoScreen() {
  const { t } = useTranslation();
  const fieldInfo =
    useJourneyStore(
      (state) => state.journeyData.transactionFieldsSendResponse?.fieldInfo
    ) ?? [];
  const senderInfo = useJourneyStore((state) => state.journeyData.senderInfo);
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();

  const senderNameFields = useMemo(() => {
    return fieldInfo
      .filter((f: FieldInfo) => f.field.startsWith("sender.name."))
      .sort(
        (a, b) =>
          Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
      );
  }, [fieldInfo]);

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      senderNameFields.forEach((f) => {
        if (next[f.field] !== undefined) return;
        const part = f.field.replace("sender.name.", "");
        if (part === "firstName") next[f.field] = senderInfo.firstName;
        else if (part === "middleName") next[f.field] = senderInfo.middleName ?? "";
        else if (part === "lastName") next[f.field] = senderInfo.lastName;
        else if (part === "secondLastName") next[f.field] = senderInfo.secondLastName ?? "";
      });
      return next;
    });
  }, [senderNameFields, senderInfo]);

  const getLabel = (field: FieldInfo) => {
    const label = field.fieldLabel || field.field;
    if (field.required) return label;
    return `${label} (optional)`;
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    senderNameFields.forEach((f) => {
      newErrors[f.field] = validateField(
        fieldInfo,
        f.field,
        values[f.field] ?? ""
      );
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    updateJourneyData({
      senderInfo: {
        firstName: values["sender.name.firstName"] ?? senderInfo.firstName,
        middleName: values["sender.name.middleName"] ?? senderInfo.middleName,
        lastName: values["sender.name.lastName"] ?? senderInfo.lastName,
        secondLastName: values["sender.name.secondLastName"] ?? senderInfo.secondLastName,
      },
    });
    goNext();
  };

  useStepAction(handleNext);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="sender-info-screen"
    >
      <Text style={styles.title}>{t("SENDER_NAME_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_NAME_SUBTITLE")}</Text>

      {senderNameFields.map((field, index) => {
        const max = getFieldMax(fieldInfo, field.field);
        const min = getFieldMin(fieldInfo, field.field);
        return (
          <View key={field.field}>
            <FloatingTextInputComponent
              testID={`sendMoney-${field.field}`}
              label={getLabel(field)}
              value={values[field.field] ?? ""}
              isAutoFocus={index === 0}
              maxLength={max ? Number(max) : undefined}
              minLength={min ? Number(min) : undefined}
              onChangeText={(val) => {
                setValues((prev) => ({ ...prev, [field.field]: val }));
                if (errors[field.field]) {
                  setErrors((prev) => ({ ...prev, [field.field]: "" }));
                }
              }}
              errorText={errors[field.field]}
              blockPersonNameAutofill
            />
            {field.required && field.helpTextLong ? (
              <Text style={styles.helpText}>{field.helpTextLong}</Text>
            ) : null}
          </View>
        );
      })}

      <View style={styles.hintBox}>
        <IdCard width={24} height={24} />
        <Text style={styles.hintText}>
          {t("MAKE_SURE_TO_USE_NAME_ON_YOUR_LEGAL_ID")}
        </Text>
      </View>

    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  helpText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.subTitleColor,
    lineHeight: 16,
    marginTop: 4,
    marginBottom: 8,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 24,
    backgroundColor: Colors.white,
    marginBottom: 24,
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16,
  },
});
